import * as React from "react";
import { RefreshCw, Save, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { SaveConflictChoice, SaveConflictVariant } from "../../services/storageService";

export interface SaveConflictModalProps {
  isOpen: boolean;
  lastKnownFileName: string;
  remoteFileName: string;
  variant: SaveConflictVariant;
  onChoice: (choice: SaveConflictChoice) => void;
}

const SaveConflictModal: React.FC<SaveConflictModalProps> = ({
  isOpen,
  lastKnownFileName,
  remoteFileName,
  variant,
  onChoice,
}) => {
  const choiceCommittedRef = React.useRef(false);

  React.useEffect(() => {
    if (isOpen) {
      choiceCommittedRef.current = false;
    }
  }, [isOpen]);

  const commit = (choice: SaveConflictChoice) => {
    choiceCommittedRef.current = true;
    onChoice(choice);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !choiceCommittedRef.current) {
          onChoice("cancel");
        }
      }}
    >
      <DialogContent className="max-w-[480px] z-[2100]">
        <DialogHeader>
          <DialogTitle>
            {variant === "missing-baseline"
              ? "Cloud document — confirm save"
              : "Newer document in cloud"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-1">
          <DialogDescription className="text-[15px] leading-relaxed">
            {variant === "missing-baseline" ? (
              <>
                There is already a file in storage for this template, but this session has not
                established which version matches your Word document (for example after reloading the
                add-in). Uploading could replace the latest cloud file with content Word does not
                match. Prefer loading the latest from the cloud unless you are sure.
              </>
            ) : (
              <>
                The cloud has a newer version than the one this add-in last synced. Saving now could
                publish an older copy as the latest file and hide newer work (for example from the
                agent or web).
              </>
            )}
          </DialogDescription>
          <div className="text-[13px] text-muted-foreground space-y-1 rounded-md border border-border px-3 py-2 font-mono">
            <div>
              <span className="text-foreground/80">
                {variant === "missing-baseline"
                  ? "Session baseline (last known file): "
                  : "Your last saved file: "}
              </span>
              {lastKnownFileName}
            </div>
            <div>
              <span className="text-foreground/80">Latest in storage: </span>
              {remoteFileName}
            </div>
          </div>
        </div>
        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-end w-full">
          <Button
            type="button"
            variant="outline"
            onClick={() => commit("cancel")}
            className="whitespace-nowrap"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => commit("overwrite")}
            className="whitespace-nowrap"
          >
            <Save className="h-4 w-4 mr-2" />
            Upload anyway
          </Button>
          <Button type="button" onClick={() => commit("reload")} className="whitespace-nowrap">
            <RefreshCw className="h-4 w-4 mr-2" />
            Load latest
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveConflictModal;
